<template>
  <div id="app" class="careers-page">
    <Header routeValue="N" ref="header"></Header>

    <main class="careers-main">
      <section class="hero">
        <img class="hero-bg" :src="assets.hero" alt="">
        <div class="hero-mask"></div>
        <div class="hero-inner" data-reveal>
          <div class="hero-badge">Global Careers • We Are Hiring</div>
          <h1 class="hero-title">
            <span>Shape the</span>
            <span class="hero-title-accent">Future of POD</span>
          </h1>
          <p class="hero-sub">Join a global POD leader with fulfillment across Europe, US and Asia</p>
          <p class="hero-desc">Grow in an agile, fast-paced environment with steep learning, strong career opportunities and performance-driven rewards</p>
          <div class="hero-actions">
            <a class="btn-lift hero-btn hero-btn-primary" href="#open-positions" @click.prevent="scrollToAnchor('open-positions')">
              <img class="hero-btn-icon" :src="icons.briefcase" alt="">
              Explore Open Roles ↓
            </a>
            <a class="btn-lift hero-btn hero-btn-ghost" href="#apply" @click.prevent="scrollToAnchor('apply')">
              <img class="hero-btn-wa" :src="icons.whatsappHero" alt="">
              Apply via WhatsApp
            </a>
          </div>
          <div class="hero-locs">
            <span class="hero-locs-label">Global Locations:</span>
            <span class="loc-chip" v-for="loc in locations" :key="loc">
              <img class="loc-chip-icon" :src="icons.worldwide" alt="">
              {{ loc }}
            </span>
          </div>
        </div>
      </section>

      <section class="why">
        <div class="section-inner">
          <div class="why-head" data-reveal>
            <div class="why-badge">
              <img :src="icons.lightning" alt="">
              WHY JOIN US
            </div>
            <h2>Why Choose Printbelle</h2>
            <p class="section-sub">More than a paycheck — grow your career on a real global scale. We value hard work, and we back our team every step of the way</p>
          </div>
          <div class="why-grid">
            <div
              class="why-card"
              v-for="card in whyCards"
              :key="card.title"
              data-reveal
              :style="{ '--hover-color': card.color }"
            >
              <div class="why-icon">
                <img class="why-icon-light" :src="card.iconLight" alt="">
                <img class="why-icon-dark" :src="card.iconDark" alt="">
              </div>
              <div class="why-tag" :style="{ color: card.color }">{{ card.tag }}</div>
              <h3>{{ card.title }}</h3>
              <p>{{ card.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="countries" data-reveal>
        <p class="countries-title">
          SERVING USERS IN <span class="hl">18+</span> COUNTRIES &amp; REGIONS · STILL EXPANDING
        </p>
        <div
          class="marquee-viewport"
          :class="{ dragging: isDragging }"
          @mousedown="onDragStart"
          @touchstart.passive="onDragStart"
        >
          <div
            class="marquee-track"
            ref="marqueeTrack"
            :key="'track-' + trackKey"
            :class="{ paused: isDragging || isPaused }"
            :style="trackStyle"
          >
            <div class="marquee-group" v-for="n in 2" :key="'g'+n" :aria-hidden="n===2 ? 'true' : null">
              <div class="country-pill" v-for="c in countries" :key="n + c.code">
                <span class="flag-wrap"><img :src="c.flag" :alt="c.name"></span>
                <span>{{ c.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="jobs" id="open-positions">
        <div class="section-inner">
          <div class="jobs-head" data-reveal>
            <div class="jobs-badge">
              <img :src="icons.openPos" alt="">
              OPEN POSITIONS
            </div>
            <h2>Find Your Next Role with Us</h2>
            <p class="section-sub">Grow your career with a fast-moving global team</p>
          </div>

          <article
            class="job-card"
            v-for="(job, i) in jobs"
            :key="job.title"
            data-reveal
            :class="{ reverse: job.reverse }"
          >
            <div class="job-body">
              <div class="job-loc">
                <img :src="icons.pin" alt="">
                MULTIPLE LOCATIONS
              </div>
              <h3>{{ job.title }}</h3>
              <p v-if="job.meta" class="job-meta">{{ job.meta }}</p>
              <div class="job-cols" :class="{ 'no-meta': !job.meta }">
                <div>
                  <h4>
                    <img :src="icons.highlight" alt="">
                    HIGHLIGHTS
                  </h4>
                  <ul class="job-list highlights">
                    <li v-for="(item, hi) in job.highlights" :key="'h'+i+hi">
                      <img :src="icons.check" alt="">
                      <span>{{ item }}</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4>
                    <img :src="icons.req" alt="">
                    REQUIREMENTS
                  </h4>
                  <ul class="job-list requirements">
                    <li v-for="(item, ri) in job.requirements" :key="'r'+i+ri">
                      <span class="dot"></span>
                      <span>{{ item }}</span>
                    </li>
                  </ul>
                </div>
              </div>
              <a class="btn-lift job-apply" href="#apply" @click.prevent="scrollToAnchor('apply')">
                Apply Now
                <img :src="icons.arrow" alt="">
              </a>
            </div>
            <div class="job-media">
              <img :src="job.image" :alt="job.title">
            </div>
          </article>
        </div>
      </section>

      <section class="apply" id="apply">
        <div class="apply-card" data-reveal>
          <h2>Join Our Global Team Today</h2>
          <p class="apply-sub">We're hiring global talent — send your resume directly via email or WhatsApp</p>

          <div class="email-bar">
            <img class="email-icon" :src="icons.email" alt="">
            <span>hr@printbelle.com</span>
            <button
              class="email-copy-btn"
              type="button"
              @click="copyEmail"
              :aria-label="copied ? 'Copied' : 'Copy hr@printbelle.com'"
            >
              <img class="email-copy" :src="copied ? icons.check : icons.copy" alt="">
            </button>
          </div>

          <div class="apply-divider">
            <span></span>
            OR CONNECT INSTANTLY
            <span></span>
          </div>

          <a
            class="btn-lift wa-bar"
            :href="whatsappUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img :src="icons.whatsappCta" alt="">
            Apply via WhatsApp
          </a>

          <div class="qr-wrap">
            <img :src="assets.qr" alt="WhatsApp QR">
            <p class="qr-label">Scan to connect on WhatsApp</p>
          </div>

          <div class="checklist">
            <p class="checklist-title">
              <img class="checklist-icon" :src="icons.checklist" alt="">
              Quick Checklist for Your Application:
            </p>
            <p class="checklist-items">Role · Nationality · Preferred Country · Name · Email · Expected Start Date</p>
          </div>
        </div>
      </section>
    </main>

    <a
      class="fab-apply"
      :class="{ 'fab-apply-show': fabVisible }"
      href="#apply"
      @click.prevent="scrollToAnchor('apply')"
    >
      <img :src="icons.whatsapp" alt="">
      Apply Now
    </a>

    <Footer></Footer>
  </div>
</template>

<script>
import Header from '../components/Header.vue'
import Footer from '../components/Footer.vue'

export default {
  name: 'Careers',
  components: { Header, Footer },
  data () {
    return {
      copied: false,
      fabVisible: false,
      isDragging: false,
      isPaused: false,
      dragStartX: 0,
      dragOffset: 0,
      baseOffset: 0,
      resumeDelay: null,
      trackKey: 0,
      animationDuration: 40,
      whatsappUrl: 'https://wa.me/8613105926618',
      assets: {
        hero: require('../assets/careers/hero.png'),
        qr: require('../assets/careers/whatsapp-qr.png')
      },
      icons: {
        whatsapp: require('../assets/careers/icons/whatsapp.png'),
        whatsappHero: require('../assets/careers/icons/whatsapp-hero.png'),
        whatsappCta: require('../assets/careers/icons/whatsapp-cta.png'),
        briefcase: require('../assets/careers/icons/briefcase.png'),
        worldwide: require('../assets/careers/icons/worldwide.png'),
        lightning: require('../assets/careers/icons/lightning.png'),
        globe: require('../assets/careers/icons/globe.png'),
        pin: require('../assets/careers/icons/pin.png'),
        openPos: require('../assets/careers/icons/open-pos.png'),
        highlight: require('../assets/careers/icons/highlight.png'),
        req: require('../assets/careers/icons/req.png'),
        check: require('../assets/careers/icons/check.png'),
        email: require('../assets/careers/icons/email.png'),
        copy: require('../assets/careers/icons/copy.png'),
        arrow: require('../assets/careers/icons/arrow.png'),
        checklist: require('../assets/careers/icons/checklist.png')
      },
      locations: ['Xiamen (HQ)', 'Vietnam', 'Hong Kong', 'US / EU Remote'],
      whyCards: [
        {
          tag: 'GLOBAL OPPORTUNITIES',
          title: 'Global Exposure & Growth',
          color: '#2563EB',
          desc: 'Work with partners and creators in 100+ countries. Get real opportunities for global projects, overseas travel, and cross-border collaboration as we expand worldwide.',
          iconLight: require('../assets/careers/icons/person.png'),
          iconDark: require('../assets/careers/icons/globe.png')
        },
        {
          tag: 'FAST PROMOTION',
          title: 'Fast-Track Promotion & Career Growth',
          color: '#7C3AED',
          desc: 'In our fast-growing business, new opportunities pop up every day. Top performers move up quickly — here, your skills and results drive your promo-\ntion, not your seniority.',
          iconLight: require('../assets/careers/icons/rocket.png'),
          iconDark: require('../assets/careers/icons/trophy.png')
        },
        {
          tag: 'GREAT PAY & BONUSES',
          title: 'Competitive Pay & Performance Bonuses',
          color: '#059669',
          desc: 'We offer top-of-market base pay, strong commission, and performance bonuses. The value you create directly turns into real financial rewards.',
          iconLight: require('../assets/careers/icons/money1.png'),
          iconDark: require('../assets/careers/icons/money2.png')
        },
        {
          tag: 'TRAINING & SUPPORT',
          title: 'Continuous Learning & Team Support',
          color: '#FF5C01',
          desc: 'Enjoy structured training, global team resources, and industry event opportuni-\nties. Master the latest in cross-border e-commerce, smart manufacturing, and\nsupply chain tech.',
          iconLight: require('../assets/careers/icons/grad2.png'),
          iconDark: require('../assets/careers/icons/grad1.png')
        }
      ],
      jobs: [
        {
          title: 'Global Market Lead',
          meta: 'Print-on-Demand · 0-to-1 Market Launch',
          reverse: false,
          image: require('../assets/careers/job-1.png'),
          highlights: [
            'Launch new markets from scratch',
            'Lead regional growth & expansion strategy',
            'Build and lead local teams',
            'Win and grow new B2B clients',
            'Improve supply chain efficiency'
          ],
          requirements: [
            'Fluent in English and the local language',
            'Experience in expanding e-commerce\nor POD businesses',
            'Deep understanding of the local market',
            'Strong\u00A0negotiation\u00A0and\u00A0deal\u2011making\u00A0skills'
          ]
        },
        {
          title: 'Global Account Executive',
          meta: 'POD Business Development',
          reverse: true,
          image: require('../assets/careers/job-2.png'),
          highlights: [
            'Grow the local dropshipping business',
            'Connect and work with local partners\n(logistics, payments, influencers)',
            'Build\u00A0&\u00A0maintain\u00A0strong\u00A0partner\u00A0relationships',
            'Help expand into new local markets'
          ],
          requirements: [
            'Fluent English; native speakers preferred',
            'Bachelor’s degree or above',
            'Experience in POD or cross-border\ne-commerce',
            'Great communication and people skills'
          ]
        },
        {
          title: 'Global HR & Operations Specialist',
          meta: '',
          reverse: false,
          image: require('../assets/careers/job-3.png'),
          highlights: [
            'Manage the full hiring process',
            'Coordinate HR operations across\ndifferent locations',
            'Ensure compliance with local labor laws',
            'Handle daily office administration',
            'Help build a positive team culture'
          ],
          requirements: [
            'Degree in HR, Business, or a related field',
            'Fluent in English and the local language',
            '1–3 years of HR experience',
            'Great attention to detail and strong\nwork ethic'
          ]
        }
      ],
      countries: [
        { code: 'au', name: 'Australia', flag: require('../assets/careers/flags/au.png') },
        { code: 'br', name: 'Brazil', flag: require('../assets/careers/flags/br.png') },
        { code: 'ca', name: 'Canada', flag: require('../assets/careers/flags/ca.png') },
        { code: 'cn', name: 'China', flag: require('../assets/careers/flags/cn.png') },
        { code: 'fr', name: 'France', flag: require('../assets/careers/flags/fr.png') },
        { code: 'de', name: 'Germany', flag: require('../assets/careers/flags/de.png') },
        { code: 'id', name: 'Indonesia', flag: require('../assets/careers/flags/id.png') },
        { code: 'jp', name: 'Japan', flag: require('../assets/careers/flags/jp.png') },
        { code: 'my', name: 'Malaysia', flag: require('../assets/careers/flags/my.png') },
        { code: 'mx', name: 'Mexico', flag: require('../assets/careers/flags/mx.png') },
        { code: 'ph', name: 'Philippines', flag: require('../assets/careers/flags/ph.png') },
        { code: 'pl', name: 'Poland', flag: require('../assets/careers/flags/pl.png') },
        { code: 'ro', name: 'Romania', flag: require('../assets/careers/flags/ro.png') },
        { code: 'es', name: 'Spain', flag: require('../assets/careers/flags/es.png') },
        { code: 'th', name: 'Thailand', flag: require('../assets/careers/flags/th.png') },
        { code: 'ae', name: 'United Arab Emirates', flag: require('../assets/careers/flags/ae.png') },
        { code: 'gb', name: 'United Kingdom', flag: require('../assets/careers/flags/gb.png') },
        { code: 'us', name: 'United States', flag: require('../assets/careers/flags/us.png') }
      ]
    }
  },
  computed: {
    trackStyle () {
      if (this.isDragging || this.isPaused) {
        return {
          transform: `translate3d(${this.baseOffset + this.dragOffset}px, 0, 0)`,
          animation: 'none'
        }
      }
      if (this.resumeDelay !== null) {
        return { animationDelay: `${this.resumeDelay}s` }
      }
      return {}
    }
  },
  mounted () {
    window.scrollTo(0, 0)
    window.addEventListener('mousemove', this.onDragMove)
    window.addEventListener('mouseup', this.onDragEnd)
    window.addEventListener('touchmove', this.onDragMove, { passive: false })
    window.addEventListener('touchend', this.onDragEnd)
    window.addEventListener('scroll', this.onFabScroll, { passive: true })
    this.onFabScroll()
    this.$nextTick(() => this.initReveal())
  },
  beforeDestroy () {
    clearTimeout(this._copyTimer)
    if (this._revealObserver) {
      this._revealObserver.disconnect()
      this._revealObserver = null
    }
    window.removeEventListener('mousemove', this.onDragMove)
    window.removeEventListener('mouseup', this.onDragEnd)
    window.removeEventListener('touchmove', this.onDragMove)
    window.removeEventListener('touchend', this.onDragEnd)
    window.removeEventListener('scroll', this.onFabScroll)
  },
  methods: {
    onFabScroll () {
      this.fabVisible = window.scrollY > 700
    },
    revealElInstant (el) {
      if (!el || el.getAttribute('data-revealed') === '1') return
      el.setAttribute('data-revealed', '1')
    },
    revealEl (el) {
      if (!el || el.getAttribute('data-revealed') === '1') return
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.setAttribute('data-revealed', '1')
        })
      })
    },
    initReveal () {
      const root = this.$el
      if (!root || typeof root.querySelectorAll !== 'function') return
      const els = root.querySelectorAll('[data-reveal]')
      if (!els.length) return

      if (typeof IntersectionObserver === 'undefined') {
        els.forEach((el) => this.revealEl(el))
        return
      }

      this._revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          this.revealEl(entry.target)
          this._revealObserver.unobserve(entry.target)
        })
      }, { threshold: 0.12 })

      els.forEach((el) => {
        const rect = el.getBoundingClientRect()
        if (rect.bottom <= 0) {
          this.revealElInstant(el)
        } else {
          this._revealObserver.observe(el)
        }
      })
    },
    scrollToAnchor (id) {
      const el = document.getElementById(id)
      if (!el) return
      const header = document.querySelector('header')
      const headerH = header ? header.getBoundingClientRect().height : 72

      if (id === 'open-positions') {
        const countries = document.querySelector('.countries')
        const anchor = countries || el
        const rect = anchor.getBoundingClientRect()
        const y = (countries ? rect.bottom : rect.top) + window.pageYOffset - headerH
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' })
        return
      }

      if (id === 'apply') {
        const card = el.querySelector('.apply-card') || el
        const rect = card.getBoundingClientRect()
        const cardTop = rect.top + window.pageYOffset
        const visibleH = window.innerHeight - headerH
        const targetY = cardTop - headerH - Math.max(0, (visibleH - rect.height) / 2)
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' })
        return
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    copyEmail () {
      const email = 'hr@printbelle.com'
      const done = () => {
        this.copied = true
        clearTimeout(this._copyTimer)
        this._copyTimer = setTimeout(() => { this.copied = false }, 2000)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(email).then(done)
      } else {
        const input = document.createElement('input')
        input.value = email
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        done()
      }
    },
    getClientX (e) {
      return e.touches && e.touches.length ? e.touches[0].clientX : e.clientX
    },
    normalizeOffset (offset, halfWidth) {
      if (!halfWidth) return 0
      let x = offset % halfWidth
      if (x > 0) x -= halfWidth
      return x
    },
    onDragStart (e) {
      const track = this.$refs.marqueeTrack
      if (!track) return
      const halfWidth = track.scrollWidth / 2
      const style = window.getComputedStyle(track)
      const matrix = style.transform
      let current = 0
      if (matrix && matrix !== 'none') {
        const parts = matrix.match(/matrix.*\((.+)\)/)
        if (parts && parts[1]) current = parseFloat(parts[1].split(',')[4]) || 0
      }
      this.baseOffset = this.normalizeOffset(current, halfWidth)
      this.dragOffset = 0
      this.dragStartX = this.getClientX(e)
      this.isDragging = true
      this.isPaused = true
    },
    onDragMove (e) {
      if (!this.isDragging) return
      if (e.cancelable && e.type === 'touchmove') e.preventDefault()
      this.dragOffset = this.getClientX(e) - this.dragStartX
    },
    onDragEnd () {
      if (!this.isDragging) return
      const track = this.$refs.marqueeTrack
      const halfWidth = track ? track.scrollWidth / 2 : 0
      const finalOffset = this.normalizeOffset(this.baseOffset + this.dragOffset, halfWidth)
      const progress = halfWidth ? Math.abs(finalOffset) / halfWidth : 0
      this.resumeDelay = -(progress * this.animationDuration)
      this.dragOffset = 0
      this.baseOffset = 0
      this.isDragging = false
      this.isPaused = false
      this.trackKey += 1
    }
  }
}
</script>

<style scoped lang="less">
@accent: #46BCEF;
@ink: #0D162B;
@muted: #334155;
@pad: 319px;

.careers-page {
  background: #fff;
  width: 100%;

  /deep/ .service_chat {
    bottom: 110px;
  }

  .hero,
  .hero-title,
  .hero-sub,
  .hero-locs-label {
    color: #fff !important;
  }

  .hero-title-accent {
    color: transparent !important;
    -webkit-text-fill-color: transparent !important;
  }

  /* Scroll reveal — same approach as CJ careers */
  [data-reveal] {
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.8s ease, transform 0.8s ease;
    will-change: opacity, transform;
  }

  [data-reveal][data-revealed] {
    opacity: 1;
    transform: none;
    will-change: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .careers-page [data-reveal] {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

.careers-main {
  width: 100%;
}

.section-inner {
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  padding: 0 @pad;
  box-sizing: border-box;
}

.section-sub {
  margin: 24px auto 0;
  max-width: 940px;
  color: @muted;
  font-size: 16px;
  font-weight: 300;
  line-height: 28px;
  text-align: center;
}

.btn-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 34px rgba(70, 188, 239, 0.25);
  }
}

.hero {
  position: relative;
  width: 100%;
  height: 673px;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  text-align: center;
  color: #fff;
  background: #0C1732;
}

.hero-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.hero-mask {
  position: absolute;
  inset: 0;
  background: rgba(8, 16, 40, 0.18);
  pointer-events: none;
}

.hero-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1100px;
  padding: 83px 24px 87px;
  box-sizing: border-box;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 35px;
  min-width: 391px;
  height: 44px;
  padding: 0 28px;
  border-radius: 22px;
  color: #fff;
  font-size: 23px;
  font-weight: 500;
  line-height: 1;
  box-sizing: border-box;
  border: 3px solid transparent;
  background:
    linear-gradient(#0C1836, #0C1836) padding-box,
    linear-gradient(90deg, #647CFF 0%, #46BCEF 48%, #29FADF 100%) border-box;
}

.hero-title {
  margin: 0;
  color: #fff;
  font-size: 76px;
  font-weight: 900;
  line-height: 1.05;

  > span:not(.hero-title-accent) {
    color: #fff;
  }
}

.hero-title-accent {
  background: linear-gradient(90deg, #2BF7E0 0%, #7B9CFF 55%, #C5B4FF 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.hero-sub {
  margin: 47px 0 0;
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}

.hero-desc {
  margin: 15px 0 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
  font-weight: 300;
  line-height: 1;
}

.hero-actions {
  display: flex;
  justify-content: center;
  gap: 31px;
  margin-top: 76px;
  flex-wrap: wrap;
}

.hero-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 235px;
  height: 56px;
  padding: 0 22px;
  border-radius: 15px;
  box-sizing: border-box;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}

.hero-btn-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  object-fit: contain;
}

.hero-btn-primary {
  background: linear-gradient(90deg, #637FFE 0%, #46BCEF 50%, #29FADF 100%);
  color: #0D162B;
  border: none;
  box-shadow: none;
}

.hero-btn-ghost {
  background: transparent;
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.92);
  box-shadow: none;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: #fff;
  }
}

.hero-btn-wa {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  object-fit: contain;
}

.hero-locs {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 80px;
}

.hero-locs-label {
  color: #fff;
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
  margin-right: 2px;
}

.loc-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 11px;
  height: 34px;
  min-width: 175px;
  padding: 0 18px 0 14px;
  border-radius: 17px;
  background: #252E43;
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  box-sizing: border-box;
}

.loc-chip-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
}

.why {
  padding: 79px 0 75px;
  background-color: #FAFAFD;
  background-image: radial-gradient(rgba(13, 22, 43, 0.06) 1px, transparent 1px);
  background-size: 18px 18px;
  text-align: center;
}

.why-badge,
.jobs-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 auto;
  height: 28px;
  padding: 0 16px;
  border-radius: 14px;
  border: 2px solid transparent;
  background:
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(90deg, #647CFF, #27FFDE) border-box;
  color: @ink;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  box-sizing: border-box;

  img {
    width: 11px;
    height: 12px;
    object-fit: contain;
  }
}

.why-badge {
  width: 145px;
  padding: 0;
}

.why h2,
.jobs h2 {
  margin: 16px 0 0;
  color: @ink;
  font-size: 46px;
  font-weight: 800;
  line-height: 1.1;
  text-align: center;
}

.why .section-sub {
  margin-top: 20px;
  max-width: none;
  white-space: nowrap;
  line-height: 28px;
}

.jobs .section-sub {
  margin-top: 16px;
  margin-bottom: 0;
  line-height: 28px;
}

.jobs-head + .job-card {
  margin-top: 40px;
}

.why-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 30px;
  row-gap: 45px;
  width: 100%;
  max-width: 1280px;
  margin: 47px auto 0;
  box-sizing: border-box;
}

.why-card {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 273px;
  padding: 32px 22px 29px;
  border-radius: 19px;
  background: #fff;
  border: 1px solid #E2E8F0;
  box-shadow: none;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  transition: box-shadow 0.6s ease, border-color 0.6s ease, background-color 0.6s ease;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 19px;
    border: 1.5px solid var(--hover-color, #2563EB);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.6s ease;
  }

  &:hover {
    background: #F8FAFF;
    border-color: transparent;
    box-shadow: 0 4px 12px rgba(3, 31, 94, 0.1);

    &::after {
      opacity: 1;
    }
  }
}

.why-icon {
  position: relative;
  width: 54px;
  height: 54px;
  margin-bottom: 25px;

  img {
    position: absolute;
    left: 0;
    top: 0;
    width: 54px;
    height: 54px;
    object-fit: contain;
    transition: opacity 0.6s ease;
  }

  .why-icon-light {
    opacity: 1;
  }

  .why-icon-dark {
    opacity: 0;
  }
}

.why-card:hover .why-icon {
  .why-icon-light {
    opacity: 0;
  }

  .why-icon-dark {
    opacity: 1;
  }
}

.why-tag {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.4px;
  line-height: 1;
}

.why-card h3 {
  margin: 10px 0 0;
  color: @ink;
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
}

.why-card p {
  margin: 13px 0 0;
  color: #0D162B;
  font-size: 16px;
  font-weight: 300;
  line-height: 22px;
  white-space: pre-line;
}

.countries {
  background: #E1E3F6;
  padding: 41px 0 25px;
  overflow-x: hidden;
  overflow-y: visible;
}

.countries-title {
  margin: 0 0 14px;
  text-align: center;
  color: @ink;
  font-size: 18px;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.4px;

  .hl {
    color: #2563EB;
    font-weight: 700;
  }
}

.marquee-viewport {
  width: calc(100% - 436px);
  max-width: 1484px;
  margin: 0 auto;
  overflow: hidden;
  padding: 14px 0 20px;
  box-sizing: border-box;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 60px, #000 calc(100% - 60px), transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0, #000 60px, #000 calc(100% - 60px), transparent 100%);

  &.dragging {
    cursor: grabbing;
  }

  &:hover .marquee-track {
    animation-play-state: paused !important;
  }
}

.marquee-track {
  display: flex;
  width: max-content;
  animation: careers-marquee 40s linear infinite;
  will-change: transform;

  &.paused {
    animation-play-state: paused !important;
  }
}

.marquee-group {
  display: flex;
  gap: 14px;
  padding-right: 14px;
}

.country-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 50px;
  padding: 0 18px;
  border-radius: 25px;
  background: #fff;
  white-space: nowrap;
  flex-shrink: 0;
  box-sizing: border-box;
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  span {
    font-size: 16px;
    font-weight: 700;
    color: @ink;
    line-height: 1;
  }

  &:hover {
    border-color: transparent;
    box-shadow: 0 4px 12px rgba(3, 31, 94, 0.14);
  }
}

.flag-wrap {
  width: 40px;
  height: 28px;
  border-radius: 5px;
  overflow: hidden;
  background: #E2E8F0;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  img {
    width: 40px;
    height: 28px;
    object-fit: cover;
    display: block;
  }
}

@keyframes careers-marquee {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(-50%, 0, 0); }
}

.jobs {
  padding: 58px 0 83px;
  background-color: #FAFAFD;
  background-image: radial-gradient(rgba(13, 22, 43, 0.05) 1px, transparent 1px);
  background-size: 18px 18px;
  text-align: center;

  .section-inner {
    padding-left: max(20px, calc((100% - 1281px) / 2));
    padding-right: max(20px, calc((100% - 1281px) / 2));
  }
}

.job-card {
  display: flex;
  align-items: center;
  width: 1281px;
  max-width: 100%;
  height: 545px;
  margin: 42px auto 0;
  border-radius: 19px;
  background: #fff;
  border: 1px solid #E2E8F0;
  box-shadow: none;
  overflow: hidden;
  box-sizing: border-box;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.6s ease, box-shadow 0.6s ease;

  &:hover {
    border-color: #2563EB;
    box-shadow: 0 4px 12px rgba(3, 31, 94, 0.1);
  }

  &.reverse {
    flex-direction: row-reverse;

    .job-media {
      margin-right: 0;
      margin-left: 38px;
    }
  }
}

.job-body {
  flex: 1 1 auto;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  width: 754px;
  min-width: 0;
  max-width: 754px;
  padding: 43px 31px 41px 34px;
  box-sizing: border-box;
}

.job-card.reverse .job-body {
  padding: 43px 34px 41px 31px;
}

.job-loc {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 195px;
  height: 34px;
  padding: 0 14px;
  border-radius: 6px;
  background: #EFF6FF;
  color: #2563EB;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  box-sizing: border-box;

  img {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }
}

.job-body h3 {
  margin: 15px 0 0;
  color: @ink;
  font-size: 34px;
  font-weight: 700;
  line-height: 1;
}

.job-meta {
  margin: 7px 0 0;
  color: #334155;
  font-size: 16px;
  font-weight: 400;
  line-height: 1;
}

.job-cols {
  display: grid;
  grid-template-columns: 341px minmax(0, 1fr);
  column-gap: 7px;
  align-items: start;
  width: 689px;
  max-width: 100%;
  height: 237px;
  margin-top: 30px;
  padding: 21px 11px 18px 19px;
  border-radius: 19px;
  background: #F8FAFC;
  box-sizing: border-box;

  &.no-meta {
    margin-top: 34px;
    height: 260px;
  }

  h4 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 12px;
    color: @muted;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;

    img {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }
  }
}

.job-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #334155;
    font-size: 16px;
    font-weight: 300;
    line-height: 32px;
  }

  li span:last-child {
    flex: 1 1 auto;
    min-width: 0;
    white-space: pre-line;
  }

  img {
    width: 14px;
    height: 14px;
    margin-top: 9px;
    flex-shrink: 0;
  }
}

.dot {
  width: 6px;
  height: 6px;
  margin-top: 13px;
  border-radius: 50%;
  background: #2563EB;
  flex-shrink: 0;
}

.job-apply {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 38px;
  width: 152px;
  height: 50px;
  padding: 0;
  border-radius: 12px;
  background: linear-gradient(90deg, #6280FE 0%, #46BCEF 50%, #29FADF 100%);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(70, 188, 239, 0.25);

  img {
    width: 11px;
    height: 11px;
    object-fit: contain;
    flex-shrink: 0;
  }
}

.job-media {
  width: 489px;
  height: 430px;
  flex-shrink: 0;
  margin-right: 38px;
  overflow: hidden;
  border-radius: 8px;

  img {
    width: 489px;
    height: 430px;
    object-fit: cover;
    object-position: center;
    display: block;
  }
}

.apply {
  padding: 85px 24px 70px;
  background: #fff;
}

.apply-card {
  width: 100%;
  max-width: 761px;
  margin: 0 auto;
  padding: 80px 53px 44px;
  border-radius: 30px;
  background: #fff;
  box-shadow: 0 7px 19px rgba(3, 8, 51, 0.1);
  box-sizing: border-box;
  text-align: center;
  position: relative;
  overflow: visible;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 7px;
    border-radius: 30px 30px 0 0;
    background: linear-gradient(90deg, #5F87FC 0%, #46BCEF 40%, #27FFDE 70%, #5F87FC 100%);
    pointer-events: none;
  }
}

.apply-card h2 {
  margin: 0;
  color: @ink;
  font-size: 46px;
  font-weight: 800;
  line-height: 46px;
}

.apply-sub {
  margin: 4px 0 0;
  color: @muted;
  font-size: 16px;
  font-weight: 300;
  line-height: 28px;
}

.email-bar,
.wa-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  max-width: 432px;
  border: none;
  text-decoration: none;
  box-sizing: border-box;
}

.wa-bar {
  cursor: pointer;
}

.email-bar {
  justify-content: flex-start;
  gap: 19px;
  height: 76px;
  margin: 35px auto 0;
  padding: 0 28px;
  border-radius: 16px;
  background: #F8FAFC;
  color: @ink;
  font-size: 18px;
  font-weight: 700;
  line-height: 28px;
  position: relative;
  z-index: 1;
  cursor: default;

  span {
    flex: 1;
    text-align: left;
  }

  .email-icon {
    width: 29px;
    height: 23px;
    flex-shrink: 0;
    object-fit: contain;
  }

  .email-copy-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    flex-shrink: 0;
  }

  .email-copy {
    width: 15px;
    height: 17px;
    object-fit: contain;
    display: block;
  }
}

.apply-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 14px 0 0;
  color: #ADB0B6;
  font-size: 14px;
  font-weight: 500;
  line-height: 28px;

  span {
    width: 120px;
    height: 1px;
    background: rgba(173, 176, 182, 0.5);
  }
}

.wa-bar {
  height: 59px;
  margin: 17px auto 0;
  border-radius: 16px;
  background: linear-gradient(90deg, #6280FE 0%, #46BCEF 50%, #29FADF 100%);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 4px 12px rgba(70, 188, 239, 0.25);

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
}

.qr-wrap {
  width: 200px;
  height: 212px;
  margin: 27px auto 0;
  padding: 14px 18px 12px;
  border-radius: 12px;
  border: 1px solid #E2E8F0;
  background: #FAFAFD;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;

  img {
    width: 165px;
    height: 165px;
    display: block;
    flex-shrink: 0;
  }
}

.qr-label {
  margin: 3px 0 0;
  color: @muted;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
}

.checklist {
  margin: 29px auto 0;
  max-width: 655px;
  padding: 12px 39px 12px;
  border-radius: 16px;
  border: 1px dashed #E2E8F0;
  background: #F8FAFC;
  color: #0D162B;
  text-align: left;
  box-sizing: border-box;

  p {
    margin: 0;
    font-size: 14px;
    color: #0D162B;
  }

  .checklist-title {
    display: flex;
    align-items: center;
    gap: 14px;
    font-weight: 300;
  }

  .checklist-icon {
    width: 13px;
    height: 19px;
    flex-shrink: 0;
    object-fit: contain;
  }

  .checklist-items {
    margin: 0 0 0 27px;
    line-height: 22px;
    font-weight: 500;
  }
}

.fab-apply {
  position: fixed;
  right: 31px;
  bottom: 25px;
  z-index: 100;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 165px;
  height: 54px;
  border-radius: 27px;
  background: linear-gradient(90deg, #6280FE 0%, #46BCEF 50%, #29FADF 100%);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  box-shadow: 0 9px 15px rgba(5, 32, 122, 0.17);
  transform: translateY(120px);
  opacity: 0;
  pointer-events: none;
  transition: transform 0.4s, opacity 0.4s, box-shadow 0.2s;

  img {
    width: 18px;
    height: 18px;
  }

  &:hover {
    box-shadow: 0 14px 28px rgba(5, 32, 122, 0.28);
  }
}

.fab-apply-show {
  transform: translateY(0);
  opacity: 1;
  pointer-events: auto;
}

@media (prefers-reduced-motion: reduce) {
  .fab-apply {
    transition: opacity 0.2s, box-shadow 0.2s;
    transform: none;
  }

  .fab-apply:not(.fab-apply-show) {
    opacity: 0;
  }

  .fab-apply-show {
    opacity: 1;
  }
}

@media (max-width: 1400px) {
  .section-inner {
    padding: 0 48px;
  }

  .marquee-viewport {
    width: calc(100% - 96px);
  }

  .why-grid {
    grid-template-columns: 1fr;
    max-width: 625px;
    margin-left: auto;
    margin-right: auto;
    row-gap: 30px;
  }

  .why-card {
    width: 100%;
    height: 273px;
    overflow: hidden;
  }

  .why .section-sub {
    white-space: normal;
    max-width: 940px;
  }

  .job-card,
  .job-card.reverse {
    flex-direction: column;
    height: auto;
  }

  .job-body,
  .job-card.reverse .job-body {
    width: 100%;
    max-width: none;
    padding: 32px 24px;
  }

  .job-cols {
    width: 100%;
    height: auto;
    min-height: 237px;
    grid-template-columns: 1fr 1fr;
    column-gap: 12px;
    padding: 22px 18px;

    &.no-meta {
      height: auto;
      min-height: 260px;
    }
  }

  .job-media {
    width: 100%;
    height: 320px;
    margin-right: 0 !important;
    margin-left: 0 !important;

    img {
      width: 100%;
      height: 100%;
    }
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 40px;
  }

  .hero-badge {
    min-width: 0;
    width: auto;
  }

  .why h2,
  .jobs h2,
  .apply-card h2 {
    font-size: 32px;
    line-height: 1.2;
  }

  .job-cols {
    grid-template-columns: 1fr;
  }

  .section-inner {
    padding: 0 20px;
  }

  .marquee-viewport {
    width: calc(100% - 40px);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Careers.vue